import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { LiveNumber } from "@/components/LiveNumber";
import { usePageTitle } from "@/hooks/usePageTitle";
import { PsyGestionIcon } from "@/components/PsyGestionLogo";
import { useSubscription } from "@/hooks/useSubscription";

const C = {
  navy: "#1B3A5C",
  teal: "#2A9D8F",
  tealBg: "#E6F5F4",
  border: "#D6E4F0",
  muted: "#6B85A3",
  white: "#FFFFFF",
  green: "#16A34A",
};

function fmtEur(n: number) {
  return n.toLocaleString("fr-FR") + "\u00a0€";
}

type BillingCycle = "monthly" | "annual";

// ─── Pricing data per billing cycle ──────────────────────────────────────────
const MONTHLY_PRICE = 47;
const ANNUAL_PRICE = 470;
const ANNUAL_MONTHLY_EQUIV = Math.round(ANNUAL_PRICE / 12); // ~39€/mois
const ANNUAL_SAVINGS = MONTHLY_PRICE * 12 - ANNUAL_PRICE;   // 94€ économisés

type Plan = {
  name: string;
  badge: string;
  badgeColor: string;
  features: string[];
  excludedFeatures?: string[];
  note?: string;
  highlight?: boolean;
  isFree?: boolean;
};

const proFeatures = [
  "Je pilote ma rentabilité en temps réel",
  "IA Experte fiscale illimitée — 24h/24",
  "Sérénité fiscale 2035 — formulaire 2035 inclus",
  "Recettes illimitées — zéro limite de saisie",
  "Simulateur de régime illimité",
  "Comparatif multi-années",
  "Rappels URSSAF automatiques",
  "Support prioritaire par email",
];

const plans: Plan[] = [
  {
    name: "Pro",
    badge: "⭐ Le plus populaire",
    badgeColor: C.teal,
    highlight: true,
    features: proFeatures,
    note: "Aucun prélèvement avant la fin de vos 14 jours d'essai. Résiliable en un clic.",
  },
  {
    name: "Découverte",
    badge: "Pour démarrer",
    badgeColor: C.muted,
    isFree: true,
    features: [
      "Je devine mes impôts (sans aide)",
      "3 simulations/mois",
      "Gestion à l'aveugle — sans projection",
      "Saisie limitée à 10 recettes/mois",
    ],
    excludedFeatures: [
      "IA Experte fiscale illimitée",
      "Sérénité fiscale 2035",
      "Export PDF pré-déclaration 2035",
      "Comparatif multi-années",
      "Support prioritaire",
    ],
    note: "Évolutif vers Pro à tout moment sans perdre vos données",
  },
];

// ─── Billing Toggle ──────────────────────────────────────────────────────────

function BillingToggle({ cycle, onChange }: { cycle: BillingCycle; onChange: (c: BillingCycle) => void }) {
  return (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        gap: 0,
        marginBottom: 32,
      }}
    >
      <button
        onClick={() => onChange("monthly")}
        style={{
          padding: "10px 24px",
          borderRadius: "10px 0 0 10px",
          border: `2px solid ${cycle === "monthly" ? C.teal : C.border}`,
          borderRight: "none",
          backgroundColor: cycle === "monthly" ? C.tealBg : C.white,
          color: cycle === "monthly" ? C.teal : C.muted,
          fontWeight: 700,
          fontSize: 14,
          cursor: "pointer",
          transition: "all .15s",
        }}
      >
        Mensuel
      </button>
      <button
        onClick={() => onChange("annual")}
        style={{
          padding: "10px 24px",
          borderRadius: "0 10px 10px 0",
          border: `2px solid ${cycle === "annual" ? C.teal : C.border}`,
          backgroundColor: cycle === "annual" ? C.tealBg : C.white,
          color: cycle === "annual" ? C.teal : C.muted,
          fontWeight: 700,
          fontSize: 14,
          cursor: "pointer",
          transition: "all .15s",
          display: "flex",
          alignItems: "center",
          gap: 8,
        }}
      >
        Annuel
        <span
          style={{
            fontSize: 11,
            fontWeight: 700,
            padding: "2px 8px",
            borderRadius: 12,
            backgroundColor: "#DCFCE7",
            color: C.green,
          }}
        >
          -{ANNUAL_SAVINGS} €
        </span>
      </button>
    </div>
  );
}

// ─── PricingCard ──────────────────────────────────────────────────────────────

function PricingCard({
  plan,
  gainMensuel,
  cycle,
}: {
  plan: Plan;
  gainMensuel: number;
  cycle: BillingCycle;
}) {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [currentPlan, setCurrentPlan] = useState<"decouverte" | "pro">("decouverte");
  const { trialUsed } = useSubscription();

  useEffect(() => {
    let isMounted = true;
    supabase.auth.getSession().then(async ({ data: { session } }) => {
      if (!session || !isMounted) return;
      const { data } = await supabase
        .from("profils")
        .select("stripe_subscription_status")
        .eq("user_id", session.user.id)
        .maybeSingle();
      if (!isMounted) return;
      const status = data?.stripe_subscription_status;
      setCurrentPlan(status === "active" || status === "trialing" ? "pro" : "decouverte");
    }).catch(() => { if (isMounted) setCurrentPlan("decouverte"); });
    return () => { isMounted = false; };
  }, []);

  const handleClick = async () => {
    if (plan.isFree) { navigate("/auth?mode=signup"); return; }

    const priceId = cycle === "annual"
      ? import.meta.env.VITE_STRIPE_PRICE_PRO_ANNUAL
      : import.meta.env.VITE_STRIPE_PRICE_PRO;

    if (!priceId) { toast.error("Configuration paiement manquante"); return; }
    const { data: { session } } = await supabase.auth.getSession();
    if (!session) { navigate("/auth?mode=signup"); return; }
    setLoading(true);
    const { data, error } = await supabase.functions.invoke("create-checkout", {
      body: {
        priceId,
        skipTrial: cycle === "annual",
        userId: session.user.id,
        userEmail: session.user.email,
        successUrl: window.location.origin + "/dashboard?subscribed=true",
        cancelUrl: window.location.origin + "/pricing",
      },
      headers: { Authorization: `Bearer ${session.access_token}` },
    });
    if (data?.alreadySubscribed) {
      toast.info("Vous avez déjà un abonnement actif — redirection vers votre tableau de bord");
      navigate("/dashboard");
      return;
    }
    if (error || !data?.url) {
      const detail = (error as any)?.context?.error ?? (error as any)?.message ?? data?.error;
      if (import.meta.env.DEV && detail) console.error("create-checkout error:", detail);
      toast.error("Erreur — réessayez ou contactez contact@freedomarchitect.io");
      setLoading(false);
      return;
    }
    window.location.href = data.url;
  };

  // Dynamic pricing display
  const isAnnual = cycle === "annual" && plan.highlight;
  const displayPrice = plan.isFree
    ? "0 €"
    : isAnnual
      ? `${ANNUAL_MONTHLY_EQUIV} €`
      : `${MONTHLY_PRICE} €`;
  const displaySub = plan.isFree
    ? "Gratuit — Pour toujours"
    : isAnnual
      ? "/mois — facturé 470\u00a0€/an"
      : "/mois";

  return (
    <div
      style={{
        borderRadius: 20,
        border: plan.highlight ? `2px solid ${C.teal}` : `1px solid ${C.border}`,
        backgroundColor: C.white,
        padding: "32px 28px",
        display: "flex",
        flexDirection: "column",
        gap: 20,
        position: "relative",
        boxShadow: plan.highlight ? `0 4px 24px ${C.teal}22` : undefined,
      }}
    >
      {/* Badge */}
      <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center" }}>
        <span
          style={{
            display: "inline-block",
            fontSize: 11,
            fontWeight: 700,
            padding: "4px 12px",
            borderRadius: 20,
            backgroundColor: plan.highlight ? C.tealBg : "#F0F4F8",
            color: plan.badgeColor,
          }}
        >
          {plan.isFree && currentPlan === "decouverte" ? "Votre plan actuel" : plan.badge}
        </span>
        {isAnnual && (
          <span
            style={{
              fontSize: 11,
              fontWeight: 700,
              padding: "4px 10px",
              borderRadius: 20,
              backgroundColor: "#DCFCE7",
              color: C.green,
            }}
          >
            Économisez {ANNUAL_SAVINGS} €/an
          </span>
        )}
      </div>

      {/* Price */}
      <div>
        <h3 style={{ fontSize: 22, fontWeight: 800, color: C.navy, marginBottom: 4 }}>{plan.name}</h3>
        <div style={{ display: "flex", alignItems: "baseline", gap: 6, flexWrap: "wrap" }}>
          {/* Barré si annuel */}
          {isAnnual && (
            <span style={{ fontSize: 20, fontWeight: 600, color: C.muted, textDecoration: "line-through" }}>
              {MONTHLY_PRICE} €
            </span>
          )}
          <span style={{ fontSize: 36, fontWeight: 800, color: C.navy }}>{displayPrice}</span>
          <span style={{ fontSize: 14, color: C.muted }}>{displaySub}</span>
          {plan.highlight && !isAnnual && !trialUsed && (
            <span
              style={{
                fontSize: 11, fontWeight: 700, padding: "3px 10px", borderRadius: 20,
                backgroundColor: "#DCFCE7", color: C.green, marginLeft: 4,
              }}
            >
              14 jours d'essai gratuit
            </span>
          )}
        </div>
        {/* Logic Gap */}
        {plan.highlight && gainMensuel > 0 && (
          <p style={{ fontSize: 13, color: C.teal, fontWeight: 700, marginTop: 6 }}>
            vs. gain potentiel :{" "}
            <LiveNumber value={gainMensuel} fmt={(n) => fmtEur(n) + "\u00a0/ mois"} baseColor={C.teal} />
          </p>
        )}
      </div>

      {/* Features */}
      <ul style={{ listStyle: "none", padding: 0, margin: 0, display: "flex", flexDirection: "column", gap: 10, flex: 1 }}>
        {plan.features.map((f, i) => (
          <li key={i} style={{ display: "flex", gap: 10, fontSize: 14, color: C.navy }}>
            <span style={{ color: C.teal, fontWeight: 700, flexShrink: 0 }}>✓</span>
            {f}
          </li>
        ))}
        {plan.excludedFeatures?.map((f, i) => (
          <li key={`x-${i}`} style={{ display: "flex", gap: 10, fontSize: 14, color: C.muted, textDecoration: "line-through" }}>
            <span style={{ flexShrink: 0 }}>✗</span>
            {f}
          </li>
        ))}
      </ul>

      {/* CTA */}
      <div>
        <button
          onClick={handleClick}
          disabled={loading}
          style={{
            width: "100%", padding: "14px 28px", borderRadius: 12, fontWeight: 700, fontSize: 16,
            cursor: loading ? "not-allowed" : "pointer",
            border: plan.highlight ? "none" : `2px solid ${C.navy}`,
            backgroundColor: plan.highlight ? (loading ? "rgba(42,157,143,.7)" : C.teal) : "transparent",
            color: plan.highlight ? C.white : C.navy,
            minHeight: 48,
          }}
        >
          {loading
            ? "Redirection..."
            : plan.highlight
              ? (cycle === "annual" ? "S'abonner au Plan Pro Annuel →" : trialUsed ? "S'abonner au Plan Pro Mensuel →" : "Commencer mon essai gratuit →")
              : currentPlan === "decouverte" ? "Plan actuel : Découverte" : "Commencer gratuitement →"}
        </button>

        {plan.highlight && (
          <p style={{ marginTop: 8, fontSize: 11, color: C.muted, textAlign: "center", fontStyle: "italic" }}>
            {cycle === "annual"
              ? "Accès immédiat à toutes les fonctionnalités Pro. Résiliable en un clic."
              : trialUsed ? "Accès immédiat à toutes les fonctionnalités Pro. Résiliable en un clic." : "Aucun prélèvement avant la fin de vos 14 jours d'essai. Résiliable en un clic."}
          </p>
        )}
        {plan.isFree && plan.note && (
          <p style={{ marginTop: 8, fontSize: 11, color: C.muted, textAlign: "center", fontStyle: "italic" }}>
            {plan.note}
          </p>
        )}

        {/* Friction reduction — Pro only */}
        {plan.highlight && (
          <div style={{ marginTop: 20, display: "flex", flexDirection: "column", gap: 12 }}>
            <div
              style={{
                backgroundColor: "#F7FAFE",
                borderRadius: 12,
                padding: "14px 16px",
                borderLeft: `3px solid ${C.teal}`,
              }}
            >
              <p style={{ fontSize: 13, color: C.navy, lineHeight: 1.7, fontStyle: "italic", margin: "0 0 6px" }}>
                "Passer au BNC Classique avec PsyGestion m'a fait économiser 4&nbsp;100&nbsp;€ dès la
                première année. L'abonnement a été rentabilisé en 4&nbsp;jours."
              </p>
              <p style={{ fontSize: 11, fontWeight: 700, color: C.muted, margin: 0 }}>— Une Psy anonyme</p>
            </div>
            <p style={{ fontSize: 12, textAlign: "center", color: C.muted, margin: 0 }}>
              🔒 Sans engagement · Annulation en 1 clic · Données supprimées sur demande
            </p>
            <p style={{ fontSize: 12, textAlign: "center", color: C.navy, fontStyle: "italic", margin: 0 }}>
              💙 Conçu avec passion par un fils pour aider sa mère psychologue et toutes ses consœurs.
            </p>
          </div>
        )}
      </div>
    </div>
  );
}

// ─── Page Pricing ─────────────────────────────────────────────────────────────

export default function Pricing() {
  usePageTitle("Tarifs — Réforme 2026 & Sérénité Fiscale");
  const navigate = useNavigate();
  const [gainMensuel, setGainMensuel] = useState(0);
  const [cycle, setCycle] = useState<BillingCycle>("annual");

  useEffect(() => {
    let computed = 287;
    try {
      const saved = localStorage.getItem("psygestion_landing_sim");
      if (saved) {
        const { tarif, patients, charges, semaines } = JSON.parse(saved);
        if (tarif && patients && semaines) {
          const ca = tarif * patients * (semaines as number);
          const netMicro = Math.max(0, ca - ca * 0.234 - (charges || 0));
          const benefice = Math.max(0, ca - (charges || 0));
          const netBNC = Math.max(0, benefice - benefice * 0.40);
          const diff = netBNC - netMicro;
          if (diff > 564) computed = Math.round((diff - 564) / 12);
        }
      }
    } catch {}
    setGainMensuel(computed);
  }, []);

  const priceComparison = cycle === "annual"
    ? `vs. ${ANNUAL_MONTHLY_EQUIV}\u00a0€/mois pour PsyGestion (facturé ${ANNUAL_PRICE}\u00a0€/an) — ROI positif dès le 1er mois.`
    : `vs. ${MONTHLY_PRICE}\u00a0€/mois pour PsyGestion — ROI positif dès le 1er mois.`;

  return (
    <div style={{ fontFamily: "'Inter', system-ui, -apple-system, sans-serif", backgroundColor: "#F7FAFE", minHeight: "100vh" }}>
      <nav style={{ backgroundColor: C.white, borderBottom: `1px solid ${C.border}`, padding: "16px 20px" }}>
        <div style={{ maxWidth: 1100, margin: "0 auto", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          <button
            onClick={() => navigate("/")}
            style={{ display: "flex", alignItems: "center", gap: 8, background: "none", border: "none", cursor: "pointer" }}
          >
            <PsyGestionIcon size={28} />
            <span style={{ fontWeight: 700, fontSize: 16, color: C.navy }}>PsyGestion</span>
          </button>
          <button
            onClick={() => navigate("/auth?mode=login")}
            style={{
              padding: "8px 20px", borderRadius: 10, border: "none",
              backgroundColor: C.teal, color: C.white, fontWeight: 700, fontSize: 14, cursor: "pointer",
            }}
          >
            Se connecter
          </button>
        </div>
      </nav>

      <section style={{ padding: "48px 16px" }} className="sm:!p-[72px_20px]">
        <div style={{ maxWidth: 1100, margin: "0 auto" }}>
          <h1 style={{ fontSize: "clamp(28px, 4vw, 42px)", fontWeight: 800, color: C.navy, textAlign: "center", marginBottom: 12 }}>
            Choisissez votre plan
          </h1>
          <p style={{ textAlign: "center", color: C.muted, fontSize: 16, marginBottom: 24 }}>
            Commencez sur Découverte, puis passez à Pro quand vous voulez. Sans engagement.
          </p>

          {/* Toggle Mensuel / Annuel */}
          <BillingToggle cycle={cycle} onChange={setCycle} />

          {/* ROI Bandeau */}
          <div
            style={{
              backgroundColor: C.tealBg,
              border: `2px solid ${C.teal}`,
              borderRadius: 14,
              padding: "20px 28px",
              marginBottom: 40,
              textAlign: "center",
            }}
          >
            <p style={{ fontSize: 16, color: C.navy, fontWeight: 700, marginBottom: 6 }}>
              💰 Le Plan Pro s'autofinance.
            </p>
            <p style={{ fontSize: 15, color: C.navy, margin: 0 }}>
              Gain net moyen constaté :{" "}
              <LiveNumber
                value={gainMensuel}
                fmt={(n) => fmtEur(n) + "\u00a0/ mois"}
                baseColor={C.teal}
                style={{ fontSize: 22, fontWeight: 800 }}
              />
            </p>
            <p style={{ fontSize: 13, color: C.muted, marginTop: 8, marginBottom: 0 }}>
              {priceComparison}
            </p>
          </div>

          <div
            style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))", gap: 20, alignItems: "start" }}
            className="max-w-lg mx-auto sm:max-w-none"
          >
            {plans.map((p) => (
              <PricingCard key={p.name} plan={p} gainMensuel={gainMensuel} cycle={cycle} />
            ))}
          </div>

          <p style={{ textAlign: "center", color: C.muted, fontSize: 14, marginTop: 32 }}>
            Sans engagement · Résiliable à tout moment · Accès immédiat après paiement
          </p>
        </div>
      </section>
    </div>
  );
}
